import { DialogService } from '@3ddv/software-division-components/generic/dialog';
import { inject, Injectable, Type } from '@angular/core';
import { ForbiddenEventModalComponent } from '@core/modals/forbidden-event-modal/forbidden-event-modal.component';
import { AddDigitalWalletModalComponent } from '@features/pages/payment/components/add-digital-wallet-modal/add-digital-wallet-modal.component';
import type { BrnDialogRef } from '@spartan-ng/brain/dialog';
import {
  ModalData,
  PatronModel,
  SeatAvailability,
  SeatWithDefinitions,
} from '../models';
import { GaModalComponent } from './ga-modal/ga-modal.component';
import { LegalTextModalComponent } from './legal-text/legal-text.component';
import { ModalsComponent } from './modals.component';
import { RestrictedSeatsModalComponent } from './restricted-seats-modal/restricted-seats-modal.component';
import { SeatDefinitionsModalComponent } from './seat-definitions-modal/seat-definitions-modal.component';
import { TutorialWelcomeModalComponent } from './tutorial-welcome-modal/tutorial-welcome-modal.component';

export interface RestrictedSeatsModalPayload {
  func: () => void;
  seats: SeatAvailability[] | SeatWithDefinitions[];
}

/** Matches previous ngx-bootstrap `ignoreBackdropClick` and centered large dialogs. */
const DIALOG_OPTIONS = {
  closeOnBackdropClick: false,
  closeOnOutsidePointerEvents: false,
  showCloseButton: false,
} as const;

const CONTENT_CLASS_LG = 'w-full !max-w-none !p-4';
const CONTENT_CLASS_XL = 'w-full !max-w-none !p-4';
const OVERLAY_PANE_XL = {
  overlayPaneMaxWidth: 'calc(100vw - 2rem)',
  overlayPaneMaxHeight: 'calc(100vh - 2rem)',
} as const;

@Injectable({
  providedIn: 'root',
})
export class ModalsService {
  private dialogService = inject(DialogService);
  private dialogRef: BrnDialogRef<unknown> | undefined;

  openModal({ ...modalData }: ModalData): void {
    this.openWithContext(ModalsComponent, modalData, CONTENT_CLASS_LG);
  }

  openAddDigitalWalletModal(
    patron: PatronModel,
    acceptFunc: (digitalWalletList: PatronModel['digitalWalletList']) => void
  ): void {
    const context = { patron, acceptFunc };
    this.openWithContext(
      AddDigitalWalletModalComponent,
      context,
      CONTENT_CLASS_LG
    );
  }

  openGaModal({ ...modalData }: ModalData): void {
    this.openWithContext(GaModalComponent, modalData, CONTENT_CLASS_LG);
  }

  openSeatDefinitionModal(
    { seatDefinition, isAda }: SeatAvailability,
    seatId: string
  ): void {
    const context = {
      definitions: seatDefinition,
      isAda,
      seatId,
    };
    /**
     * Seat definitions: ngx-bootstrap used `modal-sm` (300px in source), but deployed GS CSS sets
     */
    this.openWithContext(
      SeatDefinitionsModalComponent,
      context,
      CONTENT_CLASS_LG
    );
  }

  openTutorialWelcomeModal(modalData: Record<string, unknown>): void {
    this.openWithContext(
      TutorialWelcomeModalComponent,
      modalData,
      CONTENT_CLASS_LG
    );
  }

  openRestrictedSeatsModal({ func, seats }: RestrictedSeatsModalPayload): void {
    const context = {
      acceptFunc: func,
      seats,
    };
    this.openWithContext(
      RestrictedSeatsModalComponent,
      context,
      CONTENT_CLASS_LG
    );
  }

  openForbiddenEventModal(): void {
    this.openWithContext(ForbiddenEventModalComponent, {}, CONTENT_CLASS_LG);
  }

  openLegalTextModal(): void {
    this.openWithContext(
      LegalTextModalComponent,
      {},
      CONTENT_CLASS_XL,
      OVERLAY_PANE_XL
    );
  }

  closeModal(): void {
    if (!this.dialogRef) {
      return;
    }
    this.dialogRef.close();
    this.dialogRef = undefined;
  }

  private openWithContext<T>(
    component: Type<T>,
    context: Record<string, unknown>,
    contentClass: string,
    overlayPane?: Partial<{
      overlayPaneWidth: string;
      overlayPaneMaxWidth: string;
      overlayPaneHeight: string;
      overlayPaneMaxHeight: string;
    }>
  ): void {
    const openResult = this.dialogService.open(component, {
      ...DIALOG_OPTIONS,
      context,
      contentClass,
      ...(overlayPane ?? {}),
    });
    this.dialogRef = openResult.reference;
  }
}
